import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent, CardActions, Grid, Typography, Skeleton, Link } from '@mui/material';
import { Check, Clear, CheckCircleOutline, ChangeCircleOutlined, CurrencyExchange, AirplaneTicketOutlined, ThumbUpAlt } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { usePurchaseDetailContext } from '../../../../context/PurchaseDetailContext';
import { formatNumber, getCurrency } from '../../../../utils/price';
import { useClientData } from '../../../../context/ClientContext';
import { useParams } from 'react-router-dom';
import { getFlexibleRatesAction } from '../../../../store/actions';
import { connect } from 'react-redux';
import { LoadingButton } from '@mui/lab';

const CardFlexi = ({ data, flexibleRatesData, errorFlexibleRates, checkoutInfo, ...props }) => {
    const { t } = useTranslation();
    const params = useParams();
    const { clientData, currencies, currentProduct } = useClientData();
    const { purchaseDetail, handleAddCrosselToCart } = usePurchaseDetailContext();

    const [selectedPlan, setSelectedPlan] = useState('');
    const isMeta = Boolean(checkoutInfo?.supplier);

    useEffect(() => {
        if (checkoutInfo) {
            if (isMeta) {
                setSelectedPlan('');
            } else {
                setSelectedPlan('flexible');
            }
        }
    }, [checkoutInfo, isMeta]);

    const [updatedPrices, setUpdatedPrices] = useState({});
    const [firstLoad, setFirstLoad] = useState(true);
    const [loading, setLoading] = useState(false);

    const termsUrl = checkoutInfo?.user_product?.products?.[0]?.parameters?.flexible_rate?.terms_and_conditions_url;

    useEffect(() => {
        if (firstLoad && data) {
            setUpdatedPrices({
                flexible: data.flexible_amount,
                total: data.total_amount
            });
            setFirstLoad(false);
        }
    }, [data])

    useEffect(() => {
        if (!firstLoad && purchaseDetail.totalAmountReal) {
            const accessToken = localStorage.getItem('jwt');
            const tokenCheckout = currentProduct?.config?.api_checkout_token || currentProduct?.config_work_unit?.api_checkout_token;

            if (params.hash && tokenCheckout && accessToken) {
                const dataToSend = {
                    hash: params.hash,
                    product: params.module,
                    processes: [
                        {
                            name: "get_flexible_rate",
                            data: {
                                total_amount: purchaseDetail.totalAmountReal
                            }
                        }
                    ]
                };
                props.getFlexibleRatesAction(accessToken, tokenCheckout, dataToSend);
                setLoading(true);
            }
        }
    }, [purchaseDetail.totalAmountReal])

    useEffect(() => {
        if (loading && flexibleRatesData) {
            const flexibleRates = flexibleRatesData?.data.find(elem => elem.name === 'get_flexible_rate')?.tasks?.get_flexible_rate;

            if (flexibleRates?.code === 'OK') {
                setUpdatedPrices({
                    flexible: flexibleRates.data?.flexible_amount,
                    total: flexibleRates.data?.total_amount
                });
            } else {
                setUpdatedPrices({});
                handleSelectRate('classic');
            }

            setLoading(false);
        }
    }, [flexibleRatesData])

    useEffect(() => {
        if (loading && errorFlexibleRates) {
            setUpdatedPrices({});
            handleSelectRate('classic');
            setLoading(false);
        }
    }, [errorFlexibleRates])

    useEffect(() => {
        if (selectedPlan === 'flexible' && updatedPrices.total) {
            handleSelectRate('flexible');
        }
    }, [updatedPrices.total])

    const handleSelectRate = (type) => {
        setSelectedPlan(type);
        handleAddCrosselToCart({
            module: 'flexibleRates',
            priceDetail: {
                id: type,
                amount: type === 'classic' ? null : updatedPrices.flexible,
                totalAmount: type === 'classic' ? null : updatedPrices.total,
            }
        });
    }

    const plans = [
        {
            id: 'info',
            type: 'info',
            infoItems: [
                {
                    text: t('checkout.common.flexiInfoText1'),
                    icon: <ChangeCircleOutlined color="primary" />
                },
                {
                    text: t('checkout.common.flexiInfoText2'),
                    icon: <AirplaneTicketOutlined color="primary" />
                },
                {
                    text: t('checkout.common.flexiInfoText3'),
                    icon: <ChangeCircleOutlined color="primary" />
                }
            ],
            footnote: t('checkout.common.flexiInfoText5')
        },
        {
            id: 'classic',
            type: 'plan',
            planName: t('checkout.common.flexiCardClassicName'),
            recommended: false,
            rows: [
                {
                    text: t('checkout.common.flexiCardClassicItem1')
                },
                {
                    text: t('checkout.common.flexiCardClassicItem2')
                },
                {
                    text: t('checkout.common.flexiCardNotIncluded'),
                    icon: <Clear color="error" />
                }
            ],
            price: purchaseDetail ? purchaseDetail.totalAmount : '0',
            handleSelect: () => handleSelectRate('classic'),
            isSelected: selectedPlan === 'classic'
        },
        {
            id: 'flexible',
            type: 'plan',
            planName: t('checkout.common.flexiCardFlexiName'),
            recommended: true,
            recommendedTitle: t('checkout.common.flexiCardFlexiRecommended'),
            rows: [
                {
                    text: t('checkout.common.flexiCardIncluded'),
                    icon: <Check color="success" />,
                },
                {
                    text: t('checkout.common.flexiCardIncluded'),
                    icon: <Check color="success" />,
                },
                {
                    text: t('checkout.common.flexiCardIncluded'),
                    icon: <Check color="success" />,
                }
            ],
            price: updatedPrices.total ? formatNumber(updatedPrices.total, 2) : '0.00',
            handleSelect: () => handleSelectRate('flexible'),
            isSelected: selectedPlan === 'flexible'
        }
    ];

    return (
        <Grid container spacing={2} className='card-flexi-container' style={{ margin: '0', width: '100%' }}>
            {plans.map((plan) => {
                if (plan.type === 'info') {
                    return (
                        <Grid
                            item
                            xs={12}
                            md={4}
                            key={plan.id}
                            className='info-column'
                        >
                            <Grid container direction='column' column={2} marginBottom={7}>
                                {plan.infoItems.map((item, index) => (
                                    <Grid item key={index} className='info-row'>
                                        {item.icon}
                                        <Typography variant='caption' fontWeight='bold'>
                                            {item.text}
                                        </Typography>
                                    </Grid>
                                ))}
                            </Grid>
                            <Typography variant='caption' className='note'>
                                {plan.footnote}
                            </Typography>
                        </Grid>
                    );
                }

                if (plan.id === 'flexible' && !updatedPrices.total) {
                    return null;
                }


                // Añadimos clase 'highlight-flexi' si viene de meta y aún no se ha seleccionado nada
                const highlightClass = plan.id === 'flexible' && isMeta && !selectedPlan ? 'highlight-flexi' : '';


                return (
                    <Grid item xs={12} md={4} key={plan.id}>
                        <Card
                            onClick={() => plan.handleSelect()}
                            style={{ cursor: 'pointer' }}
                            className={`card-product-crossell card-product-flexi ${plan.isSelected ? 'selected-flexi' : ''} ${highlightClass}`}
                        >
                            <CardHeader
                                className={`card-product-header ${plan.recommended ? 'recommended' : ''}`}
                                disableTypography
                                title={
                                    <Typography>
                                        {plan.recommended && <ThumbUpAlt color='white' />}
                                        {plan.recommendedTitle}
                                    </Typography>
                                }
                            />
                            <CardContent className='card-product-content'>
                                <Typography variant='h6' color="primary" className='product-card-title' fontWeight='bold'>
                                    {plan.planName}
                                </Typography>

                                <Grid container alignItems="center" justifyContent="center" className='price-row' spacing={1} marginBottom={2}>
                                    <Grid item>
                                        <Typography variant="h6" color="primary" className="total-price-text" fontSize={13}>
                                            {t('checkout.common.flexiCardTotalPrice')}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        {(plan.id === 'flexible' && loading) ? (
                                            <Skeleton variant='rectangular' width='150px' height='32px' />
                                        ) : (
                                            <Typography variant="h6" color="primary" className="product-price-flexi">
                                                <span className="currency">
                                                    {getCurrency(clientData?.client, currencies, purchaseDetail?.currency)}
                                                </span>&nbsp;
                                                <span className="amount">
                                                    {plan.id === 'classic'
                                                        ? formatNumber(purchaseDetail.totalAmountReal ?? 0, 2)
                                                        : plan.id === 'flexible'
                                                            ? formatNumber(updatedPrices.total, 2)
                                                            : plan.price}
                                                </span>
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>

                                <Grid container direction='column'>
                                    {plan.rows.map((row, rowIndex) => (
                                        <Grid item key={rowIndex} className='info-row'>
                                            {row.icon}
                                            <Typography variant='caption'>{row.text}</Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>

                            <CardActions className='card-product-footer'>
                                <div className='text-center card-product-footer-content'>
                                    <div className='detail-price-product'>
                                        <Typography
                                            variant='caption'
                                            color='gray'
                                            style={{
                                                visibility: plan.id === 'flexible' && plan.isSelected
                                                    ? 'visible'
                                                    : 'hidden'
                                            }}
                                        >
                                            {t('checkout.common.flexiCardTermsTitle')}
                                        </Typography>
                                        <Typography
                                            variant='caption'
                                            className='terms'
                                            style={{
                                                visibility: plan.id === 'flexible' && plan.isSelected
                                                    ? 'visible'
                                                    : 'hidden'
                                            }}
                                        >
                                            <Link href={termsUrl} target='_blank'>
                                                <Typography variant='caption' className='terms'>
                                                    {t('checkout.common.flexiCardTerms')}
                                                </Typography>
                                            </Link>
                                        </Typography>
                                    </div>

                                    <LoadingButton
                                        className='button-add-product'
                                        color='secondary'
                                        variant={plan.isSelected ? 'contained' : 'outlined'}
                                        startIcon={plan.isSelected ? <CheckCircleOutline /> : null}
                                        loading={plan.id === 'flexible' && loading}
                                        onClick={plan.handleSelect}
                                    >
                                        <span>
                                            {plan.isSelected ? t('checkout.common.chose') : t('checkout.common.choose')}
                                            &nbsp;
                                            {plan.planName}
                                        </span>
                                    </LoadingButton>
                                </div>
                            </CardActions>
                        </Card>
                    </Grid>
                );
            })}
        </Grid>
    );
};

const mapStateToProps = reducers => {
    return reducers.checkoutReducer;
};

const mapDispatchToProps = dispatch => {
    return {
        getFlexibleRatesAction: (access, tokenCheckout, data) => dispatch(getFlexibleRatesAction(access, tokenCheckout, data))
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(CardFlexi);